import { getCurrentUser } from '@/api/user'
import { getAccountContacts } from '@/api/accountEndpoints'
import { loginUser, logoutUser } from '@/api/auth'
import { PermissionEnum } from '@/app/core/enums/PermissionEnum'
import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'

// Shape matches the actual /api/users/me API response
const emptyUserData = {
  id: 0,
  name: '',
  surname: '',
  phone: null,
  email: '',
  temp_account_id: null,
  photo: null,
  last_active_at: null,
  permissions: [],
  roles: [],
  last_login: {
    id: 0,
    user_id: 0,
    email: '',
    ip: '',
    created_at: ''
  },
  account: {
    id: 0,
    manager_user_id: null,
    company_name: '',
    dba_name: null,
    mc_number: '',
    address: null,
    city: null,
    state: null,
    zip: null,
    phone: null,
    email: null,
    photo: null,
    approved_at: null,
    deleted_at: null,
    documents: [],
    permissions: []
  }
}

export const useUserStore = defineStore('user', {
  state: () => ({
    data: useStorage('user', JSON.parse(JSON.stringify(emptyUserData))),
    token: useStorage('token', ''),
    paymentContact: null
  }),

  getters: {
    getFullName: state => state.data.name + ' ' + state.data.surname,
    user: state => state.data,
    getToken: state => state.token,
    getAvatarFetchUrl: state => {
      if (!state.data.photo) {
        return "/static/icons/shared/avatar.svg";
      }
      // Use S3 bucket origin from environment
      const avatarsBucketOrigin = process.env.VUE_APP_S3_AVATARS_ORIGIN || '';
      return avatarsBucketOrigin + state.data.photo;
    },
    hasPaymentContact: state => !!(state.paymentContact?.first_name || state.paymentContact?.email || state.paymentContact?.phone),
    tokenCan: state => (permission) => {
      // Convert string array to object lookup for compatibility
      const permissionsObj = Object.create(null)
      for (const perm of state.data.permissions) {
        permissionsObj[perm] = true
      }
      return permissionsObj[permission] || permissionsObj.admin || false
    }
  },

  actions: {
    setStateAfterLogin({ data }) {
      const obj = Object.assign({}, data.user)
      // Keep permissions as string array (loadboard format)
      Object.assign(this.data, obj)
      this.token = data.token
    },

    async updateStore() {
      try {
        Object.assign(this.data, await getCurrentUser())
      } catch (error) {
        console.error('Failed to update user store:', error)
        throw error
      }
      await this.fetchPaymentContact()
    },

    // Only feeds the verification checklist, which unapproved accounts see.
    // Skip without account.manage rather than let it 403.
    async fetchPaymentContact() {
      if (this.data.account?.approved_at || !this.tokenCan(PermissionEnum.AccountManage)) {
        return
      }
      const response = await getAccountContacts({ type: 5 }).catch(() => null)
      const c = response?.data?.[0]
      if (c) {
        this.paymentContact = {
          id: c.id,
          first_name: c.contact?.first_name || '',
          phone: c.contact?.phone || '',
          email: c.contact?.email || ''
        }
      }
    },

    // The login response carries the same user payload as /users/me
    async login(dto) {
      const response = await loginUser(dto)
      this.setStateAfterLogin(response)
      await this.fetchPaymentContact()
    },

    async logout() {
      try {
        await logoutUser()
      } catch (error) {
        // Clear local session even if the API call fails
        console.error('Logout API call failed:', error)
      }
      this.cleanAll()
    },

    cleanAll() {
      this.data = JSON.parse(JSON.stringify(emptyUserData))
      this.token = ''
      this.paymentContact = null
    },

    updateUserPhoto(photoName) {
      this.data.photo = photoName
    }
  }
})
